// Nimmt Mikrofon + Systemton auf, mischt beides per Web Audio API zu einem Stream
// und lädt die Aufnahme in 5-Sekunden-Häppchen zum Server hoch.
//
// Unterschied zur früheren Desktop-Version: Dort hat der Main-Prozess den Systemton
// automatisch beigesteuert. Im Browser muss der Nutzer im Auswahldialog selbst
// „Audio teilen" anhaken. Vergisst er das, würde nur die eigene Stimme aufgenommen –
// deshalb bricht start() in dem Fall bewusst mit NoSystemAudioError ab, statt still
// weiterzulaufen. Die Oberfläche fragt dann nach, ob wirklich ohne Systemton
// aufgenommen werden soll.
import { api } from './api';

export class NoSystemAudioError extends Error {
  constructor() {
    super(
      'Kein Systemton freigegeben. Im Auswahlfenster muss „Gesamter Bildschirm" gewählt ' +
        'und „Audio teilen" angehakt werden – sonst wird der Gesprächspartner nicht aufgenommen.'
    );
    this.name = 'NoSystemAudioError';
  }
}

export class PermissionDeniedError extends Error {
  constructor(cause) {
    super(
      'Zugriff auf Mikrofon oder Bildschirm wurde abgelehnt. Bitte die Freigabe im Browser ' +
        'erlauben und es erneut versuchen.'
    );
    this.name = 'PermissionDeniedError';
    this.cause = cause;
  }
}

const CHUNK_MS = 5000;
const LIVE_SEGMENT_MS = 20000;
const UPLOAD_ATTEMPTS = 3;

// Holt Mikrofon und Systemton und gibt beides als wiederverwendbares Bündel zurück.
//
// Bewusst von der Aufnahme getrennt: Der Browser zeigt den Auswahldialog bei JEDEM
// getDisplayMedia-Aufruf – das lässt sich nicht abschalten, weil die App vorab nicht
// weiß, welche Fläche sie bekommt. Behält man das Ergebnis dagegen über mehrere
// Aufnahmen hinweg, fragt er nur einmal pro Sitzung.
export async function acquireCapture({ allowMicOnly = false } = {}) {
  let mic;
  try {
    mic = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });
  } catch (err) {
    throw new PermissionDeniedError(err);
  }

  // Systemton kommt über die Bildschirmfreigabe. Das Video brauchen wir nicht und
  // stoppen es sofort wieder – nur die Audiospur ist interessant.
  let display = null;
  try {
    const stream = await navigator.mediaDevices.getDisplayMedia({
      // displaySurface: 'monitor' öffnet den Dialog direkt auf "Gesamter Bildschirm".
      // Das spart einen Klick und verhindert vor allem, dass versehentlich ein
      // einzelnes Fenster gewählt wird – dabei geht unter Windows der Systemton verloren.
      video: { displaySurface: 'monitor' },
      audio: true,
      systemAudio: 'include',
      // Den eigenen Tab nicht anbieten: Er brächte keinen Meeting-Ton und erzeugt
      // nur eine endlose Bildschirmspiegelung.
      selfBrowserSurface: 'exclude',
    });
    stream.getVideoTracks().forEach((t) => t.stop());
    if (stream.getAudioTracks().length === 0) {
      stream.getTracks().forEach((t) => t.stop());
    } else {
      display = stream;
    }
  } catch (err) {
    mic.getTracks().forEach((t) => t.stop());
    throw new PermissionDeniedError(err);
  }

  if (!display && !allowMicOnly) {
    mic.getTracks().forEach((t) => t.stop());
    throw new NoSystemAudioError();
  }

  const capture = {
    mic,
    display,
    systemAudio: Boolean(display),
    // Wird gesetzt, wenn der Nutzer die Freigabe über die Browser-Leiste beendet
    onLost: null,
    released: false,
    release() {
      if (this.released) return;
      this.released = true;
      mic.getTracks().forEach((t) => t.stop());
      if (display) display.getTracks().forEach((t) => t.stop());
    },
  };

  if (display) {
    display.getAudioTracks().forEach((track) => {
      track.onended = () => {
        capture.released = true;
        if (capture.onLost) capture.onLost();
      };
    });
  }

  return capture;
}

export class MeetingRecorder {
  constructor(onUpdate, onCoach) {
    this.onUpdate = onUpdate;
    this.onCoach = onCoach || (() => {});
    this.status = 'idle';
    this.systemAudio = false;
    this.accumulated = 0;
    this.lastStart = 0;
    this.uploadError = null;
    this.id = null;
    this.name = null;
    this.capture = null;
    // Nur selbst geholte Freigaben werden am Ende auch wieder freigegeben –
    // eine von außen übergebene bleibt für die nächste Aufnahme bestehen.
    this.ownsCapture = false;
  }

  emit() {
    const running = this.status === 'recording' ? Date.now() - this.lastStart : 0;
    this.onUpdate({
      id: this.id,
      status: this.status,
      elapsed: this.accumulated + running,
      systemAudio: this.systemAudio,
      uploadError: this.uploadError,
    });
  }

  // opts.capture: bereits gehaltene Freigabe wiederverwenden (kein Auswahldialog).
  // opts.allowMicOnly: bewusste Entscheidung des Nutzers, ohne Systemton aufzunehmen.
  async start({ meeting = null, allowMicOnly = false, coachEnabled = true, capture = null } = {}) {
    this.coachEnabled = coachEnabled;

    // Ohne übergebene Freigabe selbst eine holen – dann erscheint der Auswahldialog.
    this.ownsCapture = !capture;
    const cap = capture || (await acquireCapture({ allowMicOnly }));
    this.capture = cap;
    this.systemAudio = cap.systemAudio;

    this.ctx = new AudioContext();
    const dest = this.ctx.createMediaStreamDestination();
    this.ctx.createMediaStreamSource(cap.mic).connect(dest);
    if (cap.display) {
      this.ctx
        .createMediaStreamSource(new MediaStream(cap.display.getAudioTracks()))
        .connect(dest);
    }

    // Erst jetzt den Datensatz anlegen – vorher könnte der Start noch scheitern
    const created = await api.beginRecording(meeting);
    this.id = created.id;
    this.name = created.name;

    // Beendet der Nutzer die Bildschirmfreigabe über die Browser-Leiste, geht der
    // Systemton verloren. Die Oberfläche wird benachrichtigt, statt stumm
    // weiterzulaufen. (Der Auslöser sitzt auf der Freigabe, nicht auf der Aufnahme –
    // so greift er auch bei einer über mehrere Aufnahmen gehaltenen Freigabe.)
    const previousLostHandler = cap.onLost;
    cap.onLost = () => {
      if (previousLostHandler) previousLostHandler();
      if (this.status !== 'idle' && this.onSystemAudioLost) this.onSystemAudioLost();
    };

    // Chunks der Reihe nach hochladen (Promise-Kette erhält die Reihenfolge)
    this.queue = Promise.resolve();
    this.recorder = new MediaRecorder(dest.stream, {
      mimeType: 'audio/webm;codecs=opus',
      audioBitsPerSecond: 128000,
    });
    this.recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) {
        const blob = e.data;
        this.queue = this.queue.then(() => this.uploadChunk(blob));
      }
    };
    this.stopped = new Promise((res) => {
      this.recorder.onstop = res;
    });
    this.recorder.start(CHUNK_MS);

    // Zweite Spur für den Live-Coach: alle 20 s ein eigenständiges Segment,
    // das sofort transkribiert wird.
    this.liveChunks = [];
    this.liveRecorder = new MediaRecorder(dest.stream, {
      mimeType: 'audio/webm;codecs=opus',
      audioBitsPerSecond: 64000,
    });
    this.liveRecorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) this.liveChunks.push(e.data);
    };
    this.liveRecorder.onstop = async () => {
      const blob = new Blob(this.liveChunks, { type: 'audio/webm' });
      this.liveChunks = [];
      if (this.coachEnabled !== false && blob.size > 4000 && this.status !== 'idle') {
        try {
          const result = await api.sendLiveSegment(this.id, await blob.arrayBuffer());
          if (result && result.tip) this.onCoach(result);
        } catch {
          /* Live-Coach ist optional – die Aufnahme läuft unabhängig weiter */
        }
      }
      if (this.status === 'recording') this.liveRecorder.start();
    };
    this.liveRecorder.start();
    this.liveTimer = setInterval(() => {
      if (this.status === 'recording' && this.liveRecorder.state === 'recording') {
        this.liveRecorder.stop(); // flusht Segment; onstop startet den nächsten Zyklus
      }
    }, LIVE_SEGMENT_MS);

    this.status = 'recording';
    this.lastStart = Date.now();
    this.timer = setInterval(() => this.emit(), 500);
    this.installUnloadGuard();
    this.emit();
  }

  // Kurze Netzaussetzer sollen keine Lücke in der Aufnahme hinterlassen.
  async uploadChunk(blob) {
    const buffer = await blob.arrayBuffer();
    for (let attempt = 1; attempt <= UPLOAD_ATTEMPTS; attempt++) {
      try {
        await api.writeChunk(this.id, buffer);
        if (this.uploadError) {
          this.uploadError = null;
          this.emit();
        }
        return;
      } catch (err) {
        if (attempt === UPLOAD_ATTEMPTS) {
          this.uploadError = err.message || String(err);
          this.emit();
          return;
        }
        await new Promise((r) => setTimeout(r, 500 * attempt));
      }
    }
  }

  // Tab schließen würde die laufende Aufnahme beenden – davor warnen.
  installUnloadGuard() {
    this.unloadGuard = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', this.unloadGuard);
  }

  removeUnloadGuard() {
    if (this.unloadGuard) {
      window.removeEventListener('beforeunload', this.unloadGuard);
      this.unloadGuard = null;
    }
  }

  pause() {
    if (this.status !== 'recording') return;
    this.recorder.pause();
    this.accumulated += Date.now() - this.lastStart;
    this.status = 'paused';
    // Live-Segment flushen; onstop startet wegen status !== 'recording' nicht neu
    if (this.liveRecorder && this.liveRecorder.state === 'recording') this.liveRecorder.stop();
    this.emit();
  }

  resume() {
    if (this.status !== 'paused') return;
    this.recorder.resume();
    this.lastStart = Date.now();
    this.status = 'recording';
    if (this.liveRecorder && this.liveRecorder.state === 'inactive') this.liveRecorder.start();
    this.emit();
  }

  async stop() {
    if (this.status === 'idle') return null;
    if (this.status === 'recording') {
      this.accumulated += Date.now() - this.lastStart;
    }
    clearInterval(this.timer);
    clearInterval(this.liveTimer);
    this.status = 'idle';
    this.removeUnloadGuard();

    if (this.liveRecorder && this.liveRecorder.state !== 'inactive') this.liveRecorder.stop();
    this.recorder.stop();
    await this.stopped;
    await this.queue; // alle Häppchen sind hochgeladen

    const recording = await api.endRecording(this.id, {
      durationMs: this.accumulated,
    });

    // Eine von außen gehaltene Freigabe bleibt bestehen, damit die nächste Aufnahme
    // ohne erneuten Auswahldialog starten kann.
    if (this.ownsCapture && this.capture) this.capture.release();
    await this.ctx.close();
    this.emit();
    return recording;
  }
}
